import {
  useTranslate,
  useTranslation,
  useUpdate,
  useList,
  useInvalidate,
  CanAccess,
} from "@refinedev/core";
import React, { useState, useEffect, useMemo } from "react";
import { Typography, Space, Card, Spin, Button, Form, Flex, Input } from "antd";
import {
  GlobeIcon,
  IdentificationCardIcon,
  SwatchesIcon,
  TranslateIcon,
  UsersThreeIcon,
  PaletteIcon,
} from "@phosphor-icons/react";
import { Select } from "@libertify/ui";
import { useOrganization } from "hooks/useOrganization";
import { SettingsItem } from "../../projects/components/SettingsItem";
import { BrandKit, ContactResponse } from "pages/media/types";
import { BrandKitPreview } from "../../components/BrandKitPreview";
import { LanguageSelect } from "components/LanguageSelect";
import { getLanguageName } from "helpers/core";
import {
  usePublicThemes,
  useOrgThemes,
} from "../../components/themeEditor/hooks/useThemeApi";

type FormData = {
  language?: string;
  contact_id?: string;
  brand_kit_id?: string;
  theme_id?: string | null;
  name?: string;
  slug?: string;
};

const SLUG_REGEX = /^[a-z0-9-]+$/;

export const PreferencesTab: React.FC = () => {
  const t = useTranslate();
  const { getLocale } = useTranslation();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);
  const { organization, organizationPreferences, preferencesLoading } =
    useOrganization({});
  const invalidate = useInvalidate();
  const organizationId = organization?.id;

  const { data: brandKits, isLoading: brandKitsLoading } = useList<BrandKit>({
    resource: `media/${organizationId}/brand_kits`,
    queryOptions: { enabled: !!organizationId },
  });

  const { data: contacts, isLoading: contactsLoading } =
    useList<ContactResponse>({
      resource: `media/${organizationId}/contacts`,
      queryOptions: { enabled: !!organizationId },
    });

  const { data: publicThemesData, isLoading: publicThemesLoading } =
    usePublicThemes();
  const { data: orgThemesData, isLoading: orgThemesLoading } =
    useOrgThemes(organizationId);

  const publicThemes = useMemo(
    () => publicThemesData?.items ?? [],
    [publicThemesData?.items]
  );
  const orgThemes = useMemo(
    () => orgThemesData?.items ?? [],
    [orgThemesData?.items]
  );

  const updateMutation = useUpdate();

  useEffect(() => {
    if (organizationPreferences) {
      form.setFieldsValue({
        language: organizationPreferences.language || "",
        contact_id: organizationPreferences.contact_id || "",
        brand_kit_id: organizationPreferences.brand_kit_id || "",
        theme_id: organizationPreferences.theme_id ?? null,
        name: organization?.name,
        slug: organization?.slug,
      });
    }
  }, [organizationPreferences, form, organization]);

  const validateSlug = (slug: string): boolean => {
    if (slug.length < 3 || slug.length > 63) {
      setSlugError(t("media.organizations.index.slugLengthError"));
      return false;
    }
    if (!SLUG_REGEX.test(slug)) {
      setSlugError(t("media.organizations.index.slugFormatError"));
      return false;
    }
    setSlugError(null);
    return true;
  };

  const handleUpdate = async (values: FormData) => {
    if (values.slug && !validateSlug(values.slug)) {
      return;
    }

    const url =
      values.name || values.slug ? `media/organizations` : `media/preferences`;

    try {
      await updateMutation.mutateAsync({
        resource: url,
        id: organizationId,
        values,
        successNotification: {
          message: t(
            "media.organizations.index.preferencesUpdatedSuccessfully"
          ),
          type: "success",
          description: t("media.organizations.index.success"),
        },
      });
      setEditing(null);

      if (values.name || values.slug) {
        await invalidate({
          resource: `api/users/me/organizations`,
          invalidates: ["all"],
        });
      }
    } catch (error: unknown) {
      setSlugError(null);
      if (
        error instanceof Object &&
        "statusCode" in error &&
        (error as { statusCode: number }).statusCode === 409
      ) {
        setSlugError(t("media.organizations.index.slugUnavailable"));
      } else {
        console.error("Update failed:", error);
      }
    }
  };

  if (!organizationId) {
    return <Spin size="large" />;
  }

  if (
    preferencesLoading ||
    brandKitsLoading ||
    contactsLoading ||
    publicThemesLoading ||
    orgThemesLoading
  ) {
    return (
      <Flex justify="center" align="center" style={{ height: "100vh" }}>
        <Spin size="large" />
      </Flex>
    );
  }

  const handleCancel = () => {
    setEditing(null);
  };

  const getOptions = () => {
    switch (editing) {
      case "Contact":
        return (
          contacts?.data.map((contact) => ({
            label: `${contact.firstname} ${contact.lastname}`,
            value: contact.id,
            data: contact,
          })) || []
        );
      case "BrandKit":
        return (
          brandKits?.data.map((brandKit) => ({
            label: brandKit.name,
            value: brandKit.id,
          })) || []
        );
      case "Theme": {
        const options: Array<
          | { label: string; value: string | null }
          | {
              label: string;
              options: Array<{ label: string; value: string }>;
            }
        > = [
          {
            label: t("media.organizations.index.none"),
            value: null,
          },
        ];
        if (publicThemes.length > 0) {
          options.push({
            label: t("media.organizations.index.publicThemes"),
            options: publicThemes.map((theme) => ({
              label: theme.name,
              value: theme.id,
            })),
          });
        }
        if (orgThemes.length > 0) {
          options.push({
            label: t("media.organizations.index.organizationThemes"),
            options: orgThemes.map((theme) => ({
              label: theme.name,
              value: theme.id,
            })),
          });
        }
        return options;
      }
      default:
        return [];
    }
  };

  const renderEditForm = () => {
    if (editing === "OrganizationName") {
      return (
        <Form.Item name="name" rules={[{ required: true, message: "" }]}>
          <Input
            size="large"
            style={{ height: 40, fontSize: 20, maxWidth: "500px" }}
          />
        </Form.Item>
      );
    }

    if (editing === "OrganizationSlug") {
      return (
        <Form.Item name="slug" rules={[{ required: true, message: "" }]}>
          <Input
            size="large"
            placeholder="my-organization"
            onChange={(e) => {
              const value = e.target.value.toLowerCase();
              form.setFieldValue("slug", value);
            }}
            style={{ height: 40, fontSize: 20, maxWidth: "500px" }}
          />
        </Form.Item>
      );
    }

    if (editing === "Language") {
      return (
        <Form.Item
          label="Select default Language"
          name="language"
          rules={[{ required: true, message: "Please select a value" }]}
        >
          <LanguageSelect style={{ maxWidth: 300 }} />
        </Form.Item>
      );
    }

    if (editing === "Theme") {
      return (
        <Form.Item label={`Select default ${editing}`} name="theme_id">
          <Select style={{ maxWidth: 300 }} options={getOptions()} />
        </Form.Item>
      );
    }

    const fieldName = editing === "Contact" ? "contact_id" : "brand_kit_id";
    return (
      <Form.Item
        label={`Select default ${editing}`}
        name={fieldName}
        rules={[{ required: true, message: "Please select a value" }]}
      >
        <Select
          style={{ maxWidth: 300 }}
          options={getOptions()}
          optionRender={(option) => {
            const brandKit = brandKits?.data.find(
              (b) => b.id === option.data.value
            );
            return (
              <Space>
                {editing === "BrandKit" && brandKit && (
                  <span role="img" aria-label={String(option.data.label)}>
                    <BrandKitPreview brandkit={brandKit} showName={false} />
                  </span>
                )}
                {option.data.label ?? option.label}
              </Space>
            );
          }}
        />
      </Form.Item>
    );
  };

  return (
    <Flex vertical gap="large" style={{ width: "100%" }}>
      {editing ? (
        <Form
          form={form}
          layout="vertical"
          initialValues={organizationPreferences}
          onFinish={handleUpdate}
        >
          {renderEditForm()}
          {slugError && (
            <Typography.Text type="danger" style={{ marginBottom: 16 }}>
              {slugError}
            </Typography.Text>
          )}
          <Space>
            <Button
              onClick={() => {
                handleCancel();
                setSlugError(null);
              }}
              size="large"
            >
              {t("media.organizations.index.cancel")}
            </Button>
            <Button
              size="large"
              type="primary"
              htmlType="submit"
              loading={updateMutation.isLoading}
              disabled={updateMutation.isLoading || !!slugError}
            >
              {t("media.organizations.index.save")}
            </Button>
          </Space>
        </Form>
      ) : (
        <Flex vertical gap="large" style={{ width: "100%" }}>
          <CanAccess resource="organization" action="edit">
            <Card>
              <SettingsItem
                title={t("media.organizations.index.organizationName")}
                icon={<IdentificationCardIcon size={24} />}
                content={organization.name}
                onClick={() => setEditing("OrganizationName")}
                editable
              />
            </Card>
          </CanAccess>
          <CanAccess resource="organization" action="edit">
            <Card>
              <SettingsItem
                title={t("media.organizations.index.organizationSlug")}
                icon={<GlobeIcon size={24} />}
                content={organization.slug}
                onClick={() => setEditing("OrganizationSlug")}
                editable
              />
            </Card>
          </CanAccess>
          <CanAccess resource="media_organization_preferences" action="edit">
            <Card>
              <SettingsItem
                title={t("media.organizations.index.defaultLanguage")}
                icon={<TranslateIcon size={24} />}
                content={
                  form.getFieldValue("language")
                    ? getLanguageName(
                        form.getFieldValue("language"),
                        getLocale() ?? "en-US",
                        true
                      )
                    : ""
                }
                onClick={() => setEditing("Language")}
                editable
              />
            </Card>
            <Card>
              <SettingsItem
                title={t("media.organizations.index.defaultContact")}
                icon={<UsersThreeIcon size={24} />}
                content={(() => {
                  const contact = contacts?.data.find(
                    (c) => c.id === form.getFieldValue("contact_id")
                  );
                  return contact
                    ? `${contact.firstname} ${contact.lastname}`
                    : "Unknown";
                })()}
                onClick={() => setEditing("Contact")}
                editable
              />
            </Card>
            <Card>
              <SettingsItem
                title={t("media.organizations.index.defaultBrandKit")}
                icon={<SwatchesIcon size={24} />}
                content={
                  brandKits?.data.find(
                    (b) => b.id === form.getFieldValue("brand_kit_id")
                  )?.name
                }
                onClick={() => setEditing("BrandKit")}
                editable
              />
            </Card>
            <Card>
              <SettingsItem
                title={t("media.organizations.index.defaultTheme")}
                icon={<PaletteIcon size={24} />}
                content={(() => {
                  const themeId = form.getFieldValue("theme_id");
                  if (!themeId) {
                    return t("media.organizations.index.notSet");
                  }
                  const theme =
                    publicThemes.find((t) => t.id === themeId) ||
                    orgThemes.find((t) => t.id === themeId);
                  return theme?.name || t("media.organizations.index.notSet");
                })()}
                onClick={() => setEditing("Theme")}
                editable
              />
            </Card>
          </CanAccess>
        </Flex>
      )}
    </Flex>
  );
};

export default PreferencesTab;
